// lib/dosActions.ts
// Satu sumber aturan tombol aksi DOS per peran dan status.
//
// CERMIN dari backend: src/dos/router.ts (peran) dan src/dos/dosStatus.ts
// (DOS_EDITABLE_CODES, DOS_DELETABLE_CODES, DOS_SUBMITTABLE_CODES,
// DOS_REVIEWABLE_CODES, DOS_CONVERTIBLE_CODES, DOS_PDF_CODES). Backend adalah
// sumber kebenaran: ia yang menolak dengan 403/409. Aturan di sini hanya
// menentukan tombol yang tampil. Kalau aturan di backend berubah, ubah juga di sini.

import { ROLE } from "./roleAccess";

export const DOS_STATUS = {
  DRAFT: "DRAFT",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  CONVERTED: "CONVERTED",
} as const;

const EDITABLE = [DOS_STATUS.DRAFT, DOS_STATUS.REJECTED] as string[];
const DELETABLE = [DOS_STATUS.DRAFT] as string[];
const SUBMITTABLE = [DOS_STATUS.DRAFT, DOS_STATUS.REJECTED] as string[];
const REVIEWABLE = [DOS_STATUS.PENDING_APPROVAL] as string[];
const CONVERTIBLE = [DOS_STATUS.APPROVED] as string[];
const PDF = [DOS_STATUS.APPROVED, DOS_STATUS.CONVERTED] as string[];

export interface DosActionFlags {
  edit: boolean;
  delete: boolean;
  submit: boolean;
  approve: boolean;
  reject: boolean;
  convert: boolean;
  pdf: boolean;
}

export const NO_DOS_ACTIONS: DosActionFlags = {
  edit: false,
  delete: false,
  submit: false,
  approve: false,
  reject: false,
  convert: false,
  pdf: false,
};

/**
 * - Sales pemilik: edit/submit (DRAFT/REJECTED), delete (DRAFT), pdf.
 * - Admin: approve/reject (PENDING_APPROVAL), convert (APPROVED), pdf.
 * - Staff: hanya pdf.
 * - pdf hanya untuk APPROVED/CONVERTED.
 * - Sales bukan pemilik / role tidak dikenal: tanpa aksi.
 */
export const getDosActions = (
  role: string | null | undefined,
  statusCode: string | null | undefined,
  isOwner: boolean
): DosActionFlags => {
  const code = statusCode ?? "";
  const pdf = PDF.includes(code);

  // Nama role dicocokkan tanpa peduli huruf besar/kecil, sama seperti roleAccess.ts.
  const normalized = Object.values(ROLE).find(
    (known) => known.toLowerCase() === (role ?? "").trim().toLowerCase()
  );

  switch (normalized) {
    case ROLE.SALES:
      if (!isOwner) return NO_DOS_ACTIONS;
      return {
        ...NO_DOS_ACTIONS,
        edit: EDITABLE.includes(code),
        delete: DELETABLE.includes(code),
        submit: SUBMITTABLE.includes(code),
        pdf,
      };

    case ROLE.ADMIN:
      return {
        ...NO_DOS_ACTIONS,
        approve: REVIEWABLE.includes(code),
        reject: REVIEWABLE.includes(code),
        convert: CONVERTIBLE.includes(code),
        pdf,
      };

    case ROLE.STAFF:
      return { ...NO_DOS_ACTIONS, pdf };

    default:
      return NO_DOS_ACTIONS;
  }
};
